import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  AddEditTimeslotFormValues,
  AddEditTimeslotModalView,
  AddEditTimeslotModalViewProps
} from './AddEditTimeslotModal.view';
import { useForm, UseFormReturn, useWatch } from 'react-hook-form';
import { FormOptions } from '../../../../constants/form';
import {
  CreateTimeslotMutationFn,
  GetCategoriesLazyQueryHookResult,
  GetSocialProfilesLazyQueryHookResult,
  GetTimeslotsLazyQueryHookResult,
  GetTimeslotsQuery,
  GetTimeslotsQueryVariables,
  TimeslotDay,
  TimeslotInput,
  UpdateTimeslotMutationFn,
  GetTimeslotsDocument,
  StartOfWeekFormatSetting
} from '../../../../models/graphql';
import { ApolloError, useApolloClient } from '@apollo/client';
import { useSelectedOrganization } from '../../../../hooks/useSelectedOrganization';
import { SubmitHandler } from 'react-hook-form/dist/types/form';
import { PostingPlanHelpers } from '../postingPlanHelpers';
import { triggerErrorToast } from '../../../../helpers/triggerErrorToast';
import { GetTimeslots_workspace_timeslots } from '../../../../models/legacy-graphql';
import { showToast } from '../../../../helpers/showToast';
import { AppContext } from '../../../../providers/AppProvider';
import { BeaconContext } from '../../../../providers/BeaconProvider';
import { getApolloErrorMessage } from 'helpers/remapApolloErrors';

export type AddEditTimeslotModalControllerProps = Omit<
  AddEditTimeslotModalViewProps,
  | 'submitted'
  | 'error'
  | 'useFormInstance'
  | 'onSubmit'
  | 'socialProfiles'
  | 'categories'
  | 'dateFormat'
  | 'isLoading'
  | 'editWarning'
  | 'modalTitle'
  | 'timeslot'
  | 'queryLoading'
> & {
  getProfiles: GetSocialProfilesLazyQueryHookResult[0];
  profilesResults: GetSocialProfilesLazyQueryHookResult[1];
  getCategories: GetCategoriesLazyQueryHookResult[0];
  categoriesResults: GetCategoriesLazyQueryHookResult[1];
  getTimeslots: GetTimeslotsLazyQueryHookResult[0];
  timeslotsResults: GetTimeslotsLazyQueryHookResult[1];
  createTimeslot: CreateTimeslotMutationFn;
  updateTimeslot: UpdateTimeslotMutationFn;
  timeslot?: GetTimeslots_workspace_timeslots;
  hourDayOfWeekTuple?: [number, number];
  filteredCategoryId?: string;
  filteredProfileId?: string;
};

export const AddEditTimeslotModalController = (props: AddEditTimeslotModalControllerProps): JSX.Element => {
  const { cache } = useApolloClient();
  const { getTimeslots, getCategories, getProfiles, startOfWeek } = props;
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [error, setError] = useState<JSX.Element | null>(null);
  const [queryLoading, setQueryLoading] = useState<boolean>(false);
  const { selectedWorkspaceId, setLimitReachedModalState } = useContext(AppContext);
  const { selectedOrganization } = useSelectedOrganization();
  const { cache: apolloCache } = useApolloClient();
  const is12HourFormat = selectedOrganization?.myUserAssignment?.settings?.timeFormat === 'HOUR_12' || false;
  const categories = useMemo(
    () => props.categoriesResults.data?.workspace.categories || [],
    [props.categoriesResults.data?.workspace.categories]
  );
  const socialProfilesResult = props.profilesResults.data?.workspace.socialProfiles;
  const timeslots = useMemo(
    () => props.timeslotsResults.data?.workspace.timeslots || [],
    [props.timeslotsResults.data?.workspace.timeslots]
  );
  const startDayOfWeek = startOfWeek ?? StartOfWeekFormatSetting.MONDAY;
  const { beacon } = useContext(BeaconContext);

  const useFormInstance: UseFormReturn = useForm({
    ...FormOptions
  });
  const watchProfiles: Array<string | boolean> | undefined = useWatch({
    control: useFormInstance.control,
    name: 'profiles' // without supply name will watch the entire form, or ['firstName', 'lastName'] to watch both,
  });
  const watchCategory: string | undefined = useWatch({
    control: useFormInstance.control,
    name: 'category' // without supply name will watch the entire form, or ['firstName', 'lastName'] to watch both,
  });

  const setFilteredProfileIfFiltered = useCallback(
    (profileId: string) => {
      const profileIndex = socialProfilesResult?.findIndex(x => x.id === profileId);

      if (profileIndex !== undefined && profileIndex > -1) {
        useFormInstance.setValue(`profiles.${profileIndex}`, profileId);
      }
    },
    [socialProfilesResult, useFormInstance]
  );

  const setDayOfWeekAndTime = useCallback(() => {
    const [hour, day] = props.hourDayOfWeekTuple || [0, 1];
    const dayOfWeek = PostingPlanHelpers.getDayFromColumn(day, startDayOfWeek);
    const hourOfDay = is12HourFormat && hour > 12 ? hour - 12 : is12HourFormat && hour === 0 ? 12 : hour;
    const strHourOfTheDay = hourOfDay < 10 ? `0${hourOfDay}` : `${hourOfDay}`;

    useFormInstance.setValue('dayOfWeek', dayOfWeek);
    useFormInstance.setValue('hourOfDay', strHourOfTheDay);
    useFormInstance.setValue('minutesOfHour', '00');

    if (is12HourFormat) {
      useFormInstance.setValue('ampm', hour >= 12 ? 'pm' : 'am');
    }
  }, [useFormInstance, is12HourFormat, props.hourDayOfWeekTuple, startDayOfWeek]);

  const setFormValuesForEdit = useCallback(
    (timeslot: GetTimeslots_workspace_timeslots) => {
      // Cloned timeslots will have an id that starts with 'clone-'
      // we remove this id so it can be treated as a new timeslot
      const id = timeslot.id.startsWith('clone-') ? '' : timeslot.id;

      useFormInstance.setValue('timeslotId', id);

      const timeTuple = timeslot.time.split(':') as [string, string];
      const [hour, minute] = timeTuple;
      const hourOfDay = is12HourFormat && +hour > 12 ? +hour - 12 : is12HourFormat && +hour === 0 ? 12 : +hour;
      const strHourOfTheDay = hourOfDay < 10 ? `0${hourOfDay}` : `${hourOfDay}`;
      useFormInstance.setValue('dayOfWeek', timeslot.day);
      useFormInstance.setValue('hourOfDay', strHourOfTheDay);
      useFormInstance.setValue('minutesOfHour', minute);
      useFormInstance.setValue('category', timeslot.category.id);

      if (is12HourFormat) {
        useFormInstance.setValue('ampm', +hour >= 12 ? 'pm' : 'am');
      }

      // Set selected profiles
      timeslot.socialProfiles.forEach(profile => {
        const profileIndex = socialProfilesResult?.findIndex(x => x.id === profile.id);

        if (profileIndex !== undefined && profileIndex > -1) {
          setFilteredProfileIfFiltered(profile.id);
        }
      });
    },
    [useFormInstance, socialProfilesResult, is12HourFormat, setFilteredProfileIfFiltered]
  );

  useEffect(() => {
    if (props.timeslot) {
      setFormValuesForEdit(props.timeslot);
    } else {
      setDayOfWeekAndTime();
    }
  }, [
    props.timeslot,
    props.profilesResults.data,
    props.filteredProfileId,
    setDayOfWeekAndTime,
    setFilteredProfileIfFiltered,
    setFormValuesForEdit
  ]);

  useEffect(() => {
    if (props.categoriesResults.error || props.timeslotsResults.error || props.profilesResults.error) {
      const error =
        props.categoriesResults.error || props.timeslotsResults.error || (props.profilesResults.error as ApolloError);
      triggerErrorToast('Error Fetching Data')(error);
    }
  }, [props.categoriesResults.error, props.timeslotsResults.error, props.profilesResults.error]);

  useEffect(() => {
    if (props.categoriesResults.data && !props.timeslot) {
      useFormInstance.setValue('category', props.filteredCategoryId);
    }
  }, [props.categoriesResults.data, props.timeslot, props.filteredCategoryId, useFormInstance]);

  useEffect(() => {
    setQueryLoading(true);
    getProfiles({
      variables: { workspaceId: selectedWorkspaceId }
    });
    getCategories({
      variables: { workspaceId: selectedWorkspaceId }
    });
    getTimeslots({
      variables: { workspaceId: selectedWorkspaceId }
    });
  }, [selectedWorkspaceId, getProfiles, getCategories, getTimeslots]);

  useEffect(() => {
    if (props.categoriesResults.data && props.timeslotsResults.data && props.profilesResults.data && queryLoading) {
      setQueryLoading(false);
    }
  }, [props.profilesResults, props.timeslotsResults, props.categoriesResults, queryLoading]);

  const onSubmit: SubmitHandler<AddEditTimeslotFormValues> = (data: AddEditTimeslotFormValues) => {
    setSubmitted(true);

    let hourFormattedTo24 = data.hourOfDay;
    if (is12HourFormat) {
      if (data.ampm === 'am' && data.hourOfDay === '12') {
        hourFormattedTo24 = '00';
      } else if ((data.ampm === 'pm' && data.hourOfDay === '12') || data.ampm === 'am') {
        hourFormattedTo24 = data.hourOfDay;
      } else {
        hourFormattedTo24 = (+data.hourOfDay + 12).toString();
      }
    }

    const timeslotToMerge: GetTimeslots_workspace_timeslots | undefined = timeslots.find(
      item =>
        item.time === `${hourFormattedTo24}:${data.minutesOfHour}` &&
        item.day === data.dayOfWeek &&
        item.category.id === data.category &&
        item.id !== data.timeslotId
    );

    const input: TimeslotInput = {
      day: data.dayOfWeek as TimeslotDay,
      time: `${hourFormattedTo24}:${data.minutesOfHour}`,
      categoryId: data.category,
      socialProfileIds: data.profiles.filter(item => item) as string[]
    };
    if (data.timeslotId) {
      props
        .updateTimeslot({
          variables: {
            timeslotId: data.timeslotId,
            input: input
          }
        })
        .then(() => {
          if (timeslotToMerge) {
            // evict from cache the target if we are merging
            const cacheId = apolloCache.identify({ id: timeslotToMerge.id, __typename: 'Timeslot' });
            if (cacheId) {
              apolloCache.evict({ id: cacheId });
              apolloCache.gc();
            }
          }
          setSubmitted(false);
          props.onClose();
        })
        .catch(e => {
          // refetch timeslots
          props.timeslotsResults
            .refetch?.({ workspaceId: selectedWorkspaceId })
            .catch(triggerErrorToast('Error refetching timeslots'));
          if (getApolloErrorMessage(e) === 'PLAN_LIMIT_EXCEEDED') {
            setLimitReachedModalState({
              isOpen: true,
              type: 'timeslot'
            });
          } else {
            triggerErrorToast('Error in updating the timeslot')(e);
          }

          setSubmitted(false);
        });
    } else {
      props
        .createTimeslot({
          variables: {
            workspaceId: selectedWorkspaceId,
            input: input
          }
        })
        .then(result => {
          if (!result.data?.createTimeslot) {
            return;
          }

          const data = cache.readQuery<GetTimeslotsQuery, GetTimeslotsQueryVariables>({
            query: GetTimeslotsDocument,
            variables: { workspaceId: selectedWorkspaceId }
          });

          if (!data || !data.workspace) {
            return;
          }

          const newData: GetTimeslotsQuery = {
            workspace: {
              __typename: 'Workspace',
              id: data.workspace.id,
              timeslots: [
                ...data.workspace.timeslots,
                {
                  __typename: 'Timeslot',
                  id: result.data.createTimeslot.id,
                  day: result.data.createTimeslot.day,
                  time: result.data.createTimeslot.time,
                  onboardingContent: result.data.createTimeslot.onboardingContent,
                  category: {
                    __typename: 'Category',
                    id: result.data.createTimeslot.category.id,
                    name: result.data.createTimeslot.category.name,
                    color: result.data.createTimeslot.category.color
                  },
                  socialProfiles: result.data.createTimeslot.socialProfiles
                }
              ]
            }
          };

          // check if we have
          const isMerged = data.workspace.timeslots.find(
            timeslot =>
              timeslot.time === input.time && timeslot.day === input.day && timeslot.category.id === input.categoryId
          );

          if (isMerged) {
            newData.workspace.timeslots = newData.workspace.timeslots.filter(timeslot => timeslot.id !== isMerged.id);
            const cachedID = cache.identify({ id: isMerged.id, __typename: 'Timeslot' }) as string;
            if (cachedID) {
              apolloCache.evict({ id: cachedID });
              apolloCache.gc();
            }
          }
          cache.writeQuery({
            query: GetTimeslotsDocument,
            variables: { workspaceId: selectedWorkspaceId },
            data: newData
          });
          setSubmitted(false);
          props.onClose();

          showToast({
            summary: 'Timeslot has been created',
            detail: 'Timeslot has been created successfully',
            severity: 'success'
          });
        })
        .catch(e => {
          if (getApolloErrorMessage(e) === 'PLAN_LIMIT_EXCEEDED') {
            setLimitReachedModalState({
              isOpen: true,
              type: 'timeslot'
            });
          } else {
            triggerErrorToast('Error in creating the timeslot')(e);
          }

          setSubmitted(false);
        });
    }
  };

  const profileCategoryCheckerReducer = useCallback(
    (prev: boolean, current: string): boolean => {
      if (!prev) return prev;
      return !!timeslots.find(
        timeslot =>
          timeslot.id !== props.timeslot?.id &&
          timeslot.category.id === props.timeslot?.category.id &&
          !!timeslot.socialProfiles.find(socProfile => socProfile.id === current)
      );
    },
    [props.timeslot, timeslots]
  );

  const categoryTimeslotId = props.timeslot?.category.id;
  useEffect(() => {
    if (watchCategory) {
      const profilesFormValue: (string | boolean)[] = useFormInstance.getValues('profiles') || [];
      const hasSelectedProfiles = profilesFormValue.filter(p => !!p).length > 0;
      const category = categories.find(category => category.id === watchCategory);

      // bypass any action if we already have selected profiles or if no category
      if (!category || hasSelectedProfiles) {
        return;
      }
      if (category.defaultSocialProfiles.length >= 1) {
        category.defaultSocialProfiles.forEach(profile => {
          const indexProfile = socialProfilesResult?.findIndex(socialProfile => socialProfile.id === profile.id);
          if (indexProfile !== undefined && indexProfile > -1) {
            setFilteredProfileIfFiltered(profile.id);
          }
        });
      } else if (
        category.defaultSocialProfiles.length === 0 &&
        categoryTimeslotId !== watchCategory &&
        !props.filteredProfileId
      ) {
        useFormInstance.setValue('profiles', Array(socialProfilesResult?.length).fill(false));
      }
    }
  }, [
    watchCategory,
    categories,
    socialProfilesResult,
    setFilteredProfileIfFiltered,
    categoryTimeslotId,
    useFormInstance,
    props.filteredProfileId
  ]);
  useEffect(() => {
    if (
      watchProfiles &&
      watchCategory &&
      props.timeslot &&
      !props.timeslot.id.startsWith('clone-') &&
      timeslots.length
    ) {
      const selectedProfiles = watchProfiles.filter(profile => profile) as string[];
      let categoryOk = true;
      let profilesOk = true;
      const removedProfiles = props.timeslot.socialProfiles
        .filter(profile => !selectedProfiles.includes(profile.id))
        .map(profile => profile.id);

      // if we change category, make sure we check that previous category profile combination exists in timeslots
      if (watchCategory !== props.timeslot.category.id) {
        categoryOk = selectedProfiles.reduce(profileCategoryCheckerReducer, true);
      }

      // if we remove a profile form the initial state, we need to check if this combination still exists
      if (removedProfiles.length) {
        profilesOk = removedProfiles.reduce(profileCategoryCheckerReducer, true);
      }

      if (!categoryOk || !profilesOk) {
        setError(
          <>
            If you change this Timeslot, any queued Posts that may need to use it will not be published, and will be
            moved to the Unqueued filter. <a onClick={() => beacon('article', '697', { type: 'modal' })}>Learn more</a>
          </>
        );
      } else {
        setError(null);
      }
    }
  }, [watchProfiles, watchCategory, props.timeslot, timeslots, profileCategoryCheckerReducer, beacon]);

  const isLoading = props.profilesResults.loading || props.categoriesResults.loading;

  const viewProps: AddEditTimeslotModalViewProps = {
    ...props,
    error,
    socialProfiles: props.profilesResults.data?.workspace.socialProfiles || [],
    categories,
    submitted,
    useFormInstance,
    onSubmit,
    isLoading,
    queryLoading,
    modalTitle: !props.timeslot
      ? 'Add Timeslot'
      : props.timeslot.id.startsWith('clone-')
      ? 'Clone Timeslot'
      : 'Edit Timeslot'
  };
  return <AddEditTimeslotModalView {...viewProps} />;
};
